import {
    getAccountTypeForSubtype,
    isValidAccountSubtype,
    isValidAccountType,
} from "@common/constants/domains/accounting/accountTypeHelpers";
import {
    Account,
    AccountGroup,
} from "@common/types/domains/accounting/account";
import { normalizeToString } from "@common/helpers/string";
import { AccountProxyObject } from "./CreateAccountSpreadsheetModal";
interface ValidateAndTransformAccountsParams {
    newData: Array<Record<string, any>>;
    prevData: AccountProxyObject[];
    existingAccounts: Account[];
    groups?: AccountGroup[];
}

const defaultEmptyRow: AccountProxyObject = {
    id: "",
    name: "",
    accountType: null,
    accountSubtype: null,
    mappedKickAccountId: null,
    parentGroupId: null,
};

// The backend rejects a group/type mismatch, so drop stale groups.
function resolveParentGroupId(
    accountType: AccountProxyObject["accountType"],
    parentGroupId: string | null,
    groups?: AccountGroup[],
): string | null {
    if (!parentGroupId || !groups) {
        return null;
    }
    const group = groups.find((candidate) => candidate.id === parentGroupId);
    if (!group) {
        return null;
    }
    // Report-leaf folding is presentation-only; the backend requires exact group/type matches.
    return group.type === accountType ? parentGroupId : null;
}

function resolveAccountTypeAndGroup(
    row: Record<string, any>,
    groups?: AccountGroup[],
): Pick<AccountProxyObject, "accountType" | "parentGroupId"> {
    if (row.accountType && isValidAccountType(row.accountType)) {
        return {
            accountType: row.accountType,
            parentGroupId: resolveParentGroupId(
                row.accountType,
                normalizeToString(row.parentGroupId) ?? null,
                groups,
            ),
        };
    }

    return { accountType: null, parentGroupId: null };
}

function normalizeRawData(
    data: Array<Record<string, any>>,
    groups?: AccountGroup[],
): AccountProxyObject[] {
    return data.map((row): AccountProxyObject => {
        const { accountType, parentGroupId } = resolveAccountTypeAndGroup(
            row,
            groups,
        );
        return {
            id: normalizeToString(row.id) ?? "",
            name: (normalizeToString(row.name) ?? "").trim(),
            accountType,
            parentGroupId,
            accountSubtype:
                row.accountSubtype && isValidAccountSubtype(row.accountSubtype)
                    ? row.accountSubtype
                    : null,
            mappedKickAccountId:
                normalizeToString(row.mappedKickAccountId) ?? null,
        };
    });
}

export function validateAndTransformAccounts({
    newData,
    prevData,
    existingAccounts,
    groups,
}: ValidateAndTransformAccountsParams): AccountProxyObject[] {
    const normalizedData = normalizeRawData(newData, groups);

    const correctedData = normalizedData.map((row, idx) => {
        const prevRow = prevData[idx] ?? defaultEmptyRow;

        const isNameChanged = row.name !== prevRow.name;
        const isTypeChanged = row.accountType !== prevRow.accountType;
        const isSubtypeChanged = row.accountSubtype !== prevRow.accountSubtype;
        const isMappedIdChanged =
            row.mappedKickAccountId !== prevRow.mappedKickAccountId;

        // Multiple cells can be changed at once.
        // If user changed name, auto-select mapped account if exact match exists
        if (isNameChanged && row.name) {
            const exactMatch = existingAccounts.find(
                (account) =>
                    account.name.toLowerCase().trim() ===
                    row.name.toLowerCase().trim(),
            );
            if (exactMatch) {
                return {
                    ...row,
                    mappedKickAccountId: exactMatch.id,
                    accountType: exactMatch.type,
                    accountSubtype: exactMatch.subtype,
                    parentGroupId: resolveParentGroupId(
                        exactMatch.type,
                        row.parentGroupId,
                        groups,
                    ),
                };
            }
        }

        // Otherwise, if mapped account is changed => auto-fill type and subtype
        if (isMappedIdChanged && row.mappedKickAccountId) {
            const mappedAccount = existingAccounts.find(
                (account) => account.id === row.mappedKickAccountId,
            );
            if (mappedAccount) {
                return {
                    ...row,
                    accountType: mappedAccount.type,
                    accountSubtype: mappedAccount.subtype,
                    parentGroupId: resolveParentGroupId(
                        mappedAccount.type,
                        row.parentGroupId,
                        groups,
                    ),
                };
            }
        }

        // Otherwise, if user changed accountSubtype => update accountType and clear mappedKickAccountId
        if (isSubtypeChanged) {
            if (!row.accountSubtype) {
                return {
                    ...row,
                    mappedKickAccountId: null,
                };
            }

            const matchingAccountType = getAccountTypeForSubtype(
                row.accountSubtype,
            );
            return {
                ...row,
                accountType: matchingAccountType,
                mappedKickAccountId: null,
            };
        }

        // Otherwise, if user updated/deleted accountType => clear accountSubtype and mappedKickAccountId
        if (isTypeChanged) {
            // We'd never keep accountSubtype or mappedKickAccountId, since they have a unique valid type which should have been on prevRow.accountType
            return {
                ...row,
                accountSubtype: null,
                mappedKickAccountId: null,
            };
        }

        return row;
    });

    return correctedData;
}
